import type { CSSProperties } from 'react';
import { BrandDots } from './brand-dots';
import { usePageTitleSlotValue } from './page-title-slot';
import { useSecondarySidebarSlotValue } from './secondary-sidebar-slot';

// Matches the secondary sidebar's own width (`w-56` on its `<aside>`, set by
// whoever publishes into the slot — today only `setup.tsx`). Kept as one
// constant here rather than read from the DOM so the header's `left` can be
// computed in pure CSS (see the `--secondary-sidebar-width` var below).
const SECONDARY_SIDEBAR_WIDTH = '224px';

/**
 * Fixed top bar spanning the main content column — the brand mark on the
 * right, clear of the sidebar. Rendered once in `AppLayout` alongside
 * `AppSidebar`/`SidebarInset`, not per-page.
 *
 * `offsetTop` shifts it below any global banner (announcement/impersonation)
 * — `AppLayout` passes the same combined height it uses as `AppShellBody`'s
 * `padTop`, so the header and the page content start at the same y instead
 * of the header staying pinned at 0 and a banner painting over it.
 *
 * The left side is a page-supplied slot (see `page-title-slot.tsx`) —
 * whatever the current page's `<PageHeader>` published there (title,
 * subtitle, count), or nothing on pages that don't use `PageHeader`.
 *
 * The sidebar toggles between `--sidebar-width` (expanded) and
 * `--sidebar-width-icon` (collapsed) by flipping `data-state` on its own
 * root div, which also carries shadcn's `peer` marker (see `Sidebar` in
 * `components/ui/sidebar.tsx`, G9: can't edit that file). `SidebarInset`
 * already rides that same peer to reposition itself on collapse; this
 * header uses the identical mechanism so the two never drift out of sync
 * — no JS, no `useSidebar()` read, just CSS tracking the sidebar's own
 * state. Below `md` the sidebar is an off-canvas Sheet instead of a
 * persistent column, so the header runs full width there.
 *
 * On top of that, `left` also adds `--secondary-sidebar-width` — 0 normally,
 * `SECONDARY_SIDEBAR_WIDTH` whenever a page has published a secondary
 * sidebar (see `secondary-sidebar-slot.tsx`, today only `/setup`) — so the
 * header starts clear of that column too instead of running across its top.
 */
export function AppHeader({ offsetTop = 0 }: { offsetTop?: number }) {
  const titleSlot = usePageTitleSlotValue();
  const secondarySidebar = useSecondarySidebarSlotValue();
  return (
    <header
      className="fixed inset-x-0 top-0 z-10 h-16 bg-card shadow-sm transition-[left] duration-200 ease-linear md:left-[calc(var(--sidebar-width)+var(--secondary-sidebar-width,0px))] md:peer-data-[state=collapsed]:left-[calc(var(--sidebar-width-icon)+var(--secondary-sidebar-width,0px))]"
      style={
        {
          top: offsetTop,
          '--secondary-sidebar-width': secondarySidebar != null ? SECONDARY_SIDEBAR_WIDTH : '0px',
        } as CSSProperties
      }
    >
      <div className="flex h-full items-center justify-between gap-4 px-4">
        <div className="min-w-0 flex-1">{titleSlot}</div>
        <div className="flex shrink-0 items-center">
          <BrandDots />
          <span className="ml-2" />
          <svg
            height="16"
            viewBox="0 0 151 26"
            fill="none"
            role="img"
            aria-label="Numera"
            className="shrink-0"
          >
            <path
              fill="currentColor"
              d="M44.6628 15.887C44.6628 16.7157 44.5214 17.4703 44.2451 18.1508C43.9689 18.838 43.5849 19.4242 43.0863 19.916C42.5877 20.4079 42.0083 20.7919 41.3413 21.0681C40.6743 21.3376 39.9533 21.4724 39.1718 21.4724C38.3902 21.4724 37.7098 21.3377 37.0427 21.0749C36.3757 20.8121 35.7963 20.4281 35.291 19.9363C34.7924 19.4444 34.3949 18.8515 34.1119 18.171C33.829 17.4905 33.6875 16.7292 33.6875 15.887V0.00674407L28.8095 0.0404336V15.9207C28.8095 17.9622 29.2475 19.7341 30.1166 21.2433C30.9857 22.7525 32.2052 23.9248 33.7616 24.7536C35.3179 25.5823 37.1236 26 39.1718 26C41.22 26 43.0256 25.5823 44.582 24.7536C46.1384 23.9248 47.3511 22.7525 48.227 21.2433C49.0961 19.7341 49.5341 17.9622 49.5341 15.9207V0.00674407H44.6561V15.887H44.6628ZM66.6945 16.9111L58.2793 0.00674407H53.9673V25.4745H58.4612V10.2679L65.8523 25.4745H67.5501L74.9411 10.2679V25.4745H79.4351V0.00674407H75.1163L66.7012 16.9111H66.6945ZM84.0974 25.4745H101.662V20.9806H88.908V14.7282H99.6408V10.2343H88.908V4.49392H101.662V0H84.0974V25.4677V25.4745ZM123.795 13.8995C124.853 12.3835 125.385 10.5577 125.385 8.42187C125.385 6.99352 125.156 5.69992 124.698 4.54107C124.239 3.37549 123.518 2.41202 122.548 1.64395C121.571 0.882613 120.332 0.377304 118.802 0.141492C118.472 0.0808545 118.102 0.0471682 117.684 0.0269557C117.266 0.0134807 116.929 0 116.68 0H105.927V25.4677H110.737V16.837H116.107L120.291 25.4677H125.742L121.14 16.0285C122.252 15.5367 123.134 14.8225 123.795 13.8927V13.8995ZM120.332 9.88391C120.197 10.396 119.954 10.8608 119.604 11.2786C119.247 11.6963 118.742 11.9927 118.102 12.1747C117.852 12.242 117.583 12.2959 117.286 12.3161C116.997 12.3364 116.72 12.3566 116.478 12.3566H110.744V4.50065H116.478C116.727 4.50065 116.997 4.51412 117.286 4.54107C117.583 4.56129 117.852 4.60845 118.102 4.68257C118.748 4.85774 119.247 5.16093 119.604 5.57865C119.954 5.99638 120.197 6.468 120.332 6.97331C120.466 7.48536 120.541 7.9772 120.541 8.43535C120.541 8.8935 120.473 9.38533 120.332 9.89738V9.88391ZM142.491 0.00674407H135.309L127.527 25.4745H132.479L134.09 20.1653H143.677L145.314 25.4745H150.266L142.485 0.00674407H142.491ZM135.457 15.6714L138.853 4.50739L142.303 15.6714H135.464H135.457ZM19.3231 0.471633C18.5012 0.175183 17.6051 0.00674407 16.6753 0.00674407H0V5.10029H4.06945V25.4745H9.15625V5.10029H16.6753C18.1306 5.10029 19.3299 6.28609 19.3299 7.75486V22.9007C19.3299 24.3224 20.482 25.4812 21.9103 25.4812H24.4167V7.74812C24.4167 6.81834 24.255 5.929 23.9518 5.10029C23.1702 2.95776 21.4657 1.25318 19.3299 0.471633H19.3231Z"
            />
          </svg>
        </div>
      </div>
    </header>
  );
}
